/*=========================================================================================
  File Name: router.js
  Description: Routes for vue-router. Lazy loading is enabled.
  Object Strucutre:
                    path => router path
                    name => router name
                    component(lazy loading) => component to load
                    meta : {
                      rule => which user can have access (ACL)
                      breadcrumb => Add breadcrumb to specific page
                      pageTitle => Display title besides breadcrumb
                    }
  ----------------------------------------------------------------------------------------
  Item Name: GCI 2020
  Author: Dtsmart
  Author URL: http://dtsmart.vn
==========================================================================================*/


import Vue from 'vue'
import Router from 'vue-router'
import store from './store/store'

Vue.use(Router)

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  scrollBehavior() {
    return { x: 0, y: 0 }
  },
  routes: [

    {
      // =============================================================================
      // MAIN LAYOUT ROUTES
      // =============================================================================
      path: '',
      component: () => import('./layouts/main/Main.vue'),
      children: [
        // =============================================================================
        // Theme Routes
        // =============================================================================
        {
          path: '/',
          name: 'home',
          component: () => import('./views/Home.vue'),
          meta: {
            requiresAuth: true,
            requirePasscode: true,
          }
        },
        {
          path: '/after-login',
          name: 'after-login',
          component: () => import('./views/AfterLogin.vue'),
          meta: {
            requiresAuth: true
          }
        },
        {
          path: '/block-time',
          name: 'blocktime',
          component: () => import('./views/appointment/BlockTimeSetting.vue'),
          meta: {
            requiresAuth: true,
            requirePasscode: true,
          }
        },
        {
          path: '/booking-staff',
          name: 'bookingstaff',
          component: () => import('./views/appointment/BookingStaffSetting.vue'),
          meta: {
            requiresAuth: true,
            requirePasscode: true,
          }
        },
        {
          path: '/promotion',
          name: 'promotion',
          component: () => import('./views/campaign/Promotion.vue'),
          meta: {
            requiresAuth: true,
            requirePasscode: true
          }
        },
        {
          path: '/reminder',
          name: 'reminder',
          component: () => import('./views/campaign/Reminder.vue'),
          meta: {
            requiresAuth: true,
            requirePasscode: true
          }
        },
        {
          path: '/birthday',
          name: 'birthday',
          component: () => import('./views/campaign/Birthday.vue'),
          meta: {
            requiresAuth: true,
            requirePasscode: true
          }
        },
        {
          path: '/new-client',
          name: 'new-client',
          component: () => import('./views/campaign/NewClient.vue'),
          meta: {
            requiresAuth: true,
            requirePasscode: true
          }
        },
        {
          path: '/service',
          name: 'service',
          component: () => import('./views/service/Service.vue'),
          meta: {
            requiresAuth: true,
            requirePasscode: true
          }
        },
        {
          path: '/customer',
          name: 'customer',
          component: () => import('./views/customer/Customer.vue'),
          meta: {
            requiresAuth: true,
            requirePasscode: true
          }
        },
        {
          path: '/group',
          name: 'group',
          component: () => import('./views/group/Group.vue'),
          meta: {
            requiresAuth: true,
            requirePasscode: true
          }
        },
        {
          path: '/staff',
          name: 'staff',
          component: () => import('./views/staff/Staff.vue'),
          meta: {
            requiresAuth: true,
            requirePasscode: true
          }
        },
        {
          path: '/review_list',
          name: 'review_list',
          component: () => import('./views/review/ReviewList.vue'),
          meta: {
            requiresAuth: true,
            requirePasscode: true
          },
          props: true,
        },
        {
          path: '/review_setting',
          name: 'review_setting',
          component: () => import('./views/review/ReviewSetting.vue'),
          meta: {
            requiresAuth: true,
            requirePasscode: true
          }
        },
        {
          path: '/staff_rating',
          name: 'staff_rating',
          component: () => import('./views/review/StaffRating.vue'),
          meta: {
            requiresAuth: true,
            requirePasscode: true,
            isGoPos: true
          }
        },
        {
          path: '/reward',
          name: 'reward',
          component: () => import('./views/reward/Reward.vue'),
          meta: {
            requiresAuth: true,
            requirePasscode: true
          }
        },
        {
          path: '/pages/app-setting',
          name: 'app-setting',
          component: () => import('./views/pages/AppSetting.vue'),
          meta: {
            requiresAuth: true,
            requirePasscode: true
          }
        },
        {
          path: '/pages/chat',
          name: 'chat',
          component: () => import('./views/pages/Chat.vue'),
          meta: {
            requiresAuth: true
          }
        },
        {
          path: '/reward-history',
          name: 'reward-history',
          component: () => import('./views/reward/RewardHistory.vue'),
          meta: {
            requiresAuth: true,
            requirePasscode: true
          },
          props: true
        },
        {
          path: '/checkout-detail',
          name: 'checkout-detail',
          component: () => import('./views/checkout/CheckoutDetail.vue'),
          meta: {
            requiresAuth: true
          },
          props: true
        },
        {
          path: 'promotion/promotion-detail',
          name: 'promotion-detail',
          component: () => import('./views/campaign/PromotionDetail.vue'),
          meta: {
            requiresAuth: true,
            requirePasscode: true
          },
          props: true
        },
        {
          path: 'reminder/reminder-detail',
          name: 'reminder-detail',
          component: () => import('./views/campaign/ReminderDetail.vue'),
          meta: {
            requiresAuth: true,
            requirePasscode: true
          },
          props: true
        },
        {
          path: 'birthday/birthday-detail',
          name: 'birthday-detail',
          component: () => import('./views/campaign/BirthdayDetail.vue'),
          meta: {
            requiresAuth: true,
            requirePasscode: true
          },
          props: true
        },
        {
          path: 'group/group_info',
          name: 'group_info',
          component: () => import('./views/group/GroupInfo.vue'),
          meta: {
            requiresAuth: true,
            requirePasscode: true
          },
          props: true
        },
      ],
    },
    // =============================================================================
    // FULL PAGE LAYOUTS
    // =============================================================================
    {
      path: '',
      component: () => import('@/layouts/full-page/FullPage.vue'),
      children: [
        // =============================================================================
        // PAGES
        // =============================================================================
        {
          path: '/pages/login',
          name: 'page-login',
          component: () => import('@/views/pages/Login.vue')
        },
        {
          path: '/pages/error-404',
          name: 'page-error-404',
          component: () => import('@/views/pages/Error404.vue')
        },
      ]
    },
    //Appointment module
    {
      path: '/appointment',
      component: () => import('./layouts/main/Main.vue'),
      children: [
        {
          path: '/',
          name: 'appointment',
          component: () => import('./views/appointment/Calendar.vue'),
          meta: {
            requiresAuth: true
          }
        },
        {
          path: '/go-booking-settings',
          name: 'go-booking-settings',
          component: () => import('./views/appointment/AppointmentSettings.vue'),
          meta: {
            requiresAuth: true,
            requirePasscode: true,
          }
        },
        // {
        //   path: '/pos-appointment',
        //   name: 'pos-appointment',
        //   component: () => import('./views/appointment/PosCalendar.vue'),
        //   meta: {
        //     requiresAuth: true
        //   }
        // },
      ],
    },
    {
      path: '/pos-appointment',
      name: 'pos-appointment',
      component: () => import('./views/appointment/PosCalendar.vue'),
      meta: {
        requiresAuth: true
      },
      // component: () => import('./layouts/main/Main.vue'),
      // children: [
      //   {
      //     path: '/',
      //     name: 'pos-appointment',
      //     component: () => import('./views/appointment/PosCalendar.vue'),
      //     meta: {
      //       requiresAuth: true
      //     }
      //   },
      // ]
    },

    //Order online module
    {
      path: '',
      component: () => import('./layouts/main/Main.vue'),
      children: [
        {
          path: '/online-order/category',
          name: 'category',
          meta: {
            requiresAuth: true
          },
          props: true,
          component: () => import('./views/order/Category.vue'),
        },
        {
          path: '/online-order/item',
          name: 'item',
          meta: {
            requiresAuth: true
          },
          props: true,
          component: () => import('./views/order/Item.vue'),
        },
        {
          path: '/online-order/order',
          name: 'order',
          meta: {
            requiresAuth: true
          },
          props: true,
          component: () => import('./views/order/Order.vue'),
        },
        {
          path: '/online-order/modifier',
          name: 'modifier',
          meta: {
            requiresAuth: true
          },
          props: true,
          component: () => import('./views/order/Modifier.vue'),
        },
        {
          path: '/online-order/invoice',
          name: 'invoice',
          meta: {
            requiresAuth: true
          },
          props: true,
          component: () => import('./views/order/Invoice.vue'),
        }
      ]
    },
    //Notification module
    {
      path: '/notify/:tab',
      component: () => import('./layouts/main/Main.vue'),
      children: [
        {
          path: '/',
          name: 'notify',
          component: () => import('./views/Notification.vue'),
          meta: {
            requiresAuth: true
          }
        },
      ],
    },
    //Checkout module
    {
      path: '/checkout',
      component: () => import('./layouts/main/Main.vue'),
      children: [
        {
          path: '/',
          name: 'checkout',
          component: () => import('./views/Checkout.vue'),
          meta: {
            requiresAuth: true
          }
        },
        {
          path: '/checkout/history',
          name: 'history',
          component: () => import('./views/History.vue'),
          meta: {
            requiresAuth: true
          }
        },
        // {
        //   path: '/checkin',
        //   name: 'checkin',
        //   component: () => import('./views/Checkin.vue'),
        //   meta: {
        //     requiresAuth: true
        //   }
        // },
      ],
    },

    //Article module
    {
      path: '/article',
      component: () => import('./layouts/main/Main.vue'),
      children: [
        {
          path: '/',
          name: 'article',
          component: () => import('./views/article/Article.vue'),
          meta: {
            requiresAuth: true
          }
        },
      ]
    },

    //GoPOS module
    {
      path: '/go-pos',
      component: () => import('./layouts/main/Main.vue'),
      children: [
        {
          path: '/go-pos/dashboard',
          name: 'go-pos-dashboard',
          component: () => import('./views/gopos/GoPOSDashboard.vue'),
          meta: {
            requiresAuth: true,
            requirePasscode: true,
            isGoPos: true
          }
        },
        {
          path: '/go-pos/income',
          name: 'income',
          component: () => import('./views/gopos/Income.vue'),
          meta: {
            requiresAuth: true,
            requirePasscode: true,
            isGoPos: true
          }
        },
        {
          path: '/go-pos/staff-daily-income',
          name: 'staff-daily-income',
          component: () => import('./views/gopos/StaffDailyIncome.vue'),
          meta: {
            requiresAuth: true,
            requirePasscode: true,
            isGoPos: true
          }
        },
        {
          path: '/go-pos/payroll',
          name: 'payroll',
          component: () => import('./views/gopos/Payroll.vue'),
          meta: {
            requiresAuth: true,
            requirePasscode: true,
            isGoPos: true
          }
        },
        {
          path: '/go-pos/timekeeping',
          name: 'timekeeping',
          component: () => import('./views/gopos/TimeKeeping.vue'),
          meta: {
            requiresAuth: true,
            requirePasscode: true,
            isGoPos: true
          }
        },
        // {
        //   path: '/go-pos/reseller',
        //   name: 'reseller',
        //   component: () => import('./views/gopos/Reseller.vue'),
        //   meta: {
        //     requiresAuth: true
        //   }
        // },
        {
          path: '/go-pos/batch-history',
          name: 'batch-history',
          component: () => import('./views/gopos/BatchHistory.vue'),
          meta: {
            requiresAuth: true,
            requirePasscode: true,
            isGoPos: true
          }
        },
        {
          path: '/go-pos/order-detail',
          name: 'go-pos-order-detail',
          component: () => import('./views/gopos/OrderDetail.vue'),
          meta: {
            requiresAuth: true,
            requirePasscode: true,
            isGoPos: true
          },
          props: true,
        },
        {
          path: '/go-pos/ticket-template',
          name: 'go-pos-ticket-template',
          component: () => import('./views/gopos/GoPOSTicketTemplate.vue'),
          meta: {
            requiresAuth: true,
            requirePasscode: true,
            isGoPos: true
          }
        },
        {
          path: '/go-pos/giftcard',
          name: 'giftcard',
          component: () => import('./views/gopos/GiftCard.vue'),
          meta: {
            requiresAuth: true,
            requirePasscode: true,
            isGoPos: true
          }
        }
      ]
    },

    // Redirect to 404 page, if no match found
    {
      path: '*',
      redirect: '/pages/error-404'
    }
  ],
})

router.beforeEach(async (to, from, next) => {
  // let token = window.location.search.split("=")[1];
  // if(localStorage.getItem('accessToken') != undefined){
  //     if(window.location.href == window.location.origin + '?token=' + localStorage.getItem('accessToken')){
  //       next({ name : 'home'})
  //     }
  //   }else{
  //   console.log('a')
  //   console.log(token)
  //   localStorage.setItem('accessToken' ,token)
  //   localStorage.setItem('endpoint', process.env.VUE_APP_API_BASE_URL);
  //   localStorage.setItem('domainId', process.env.VUE_APP_DEVELOPMENT_DOMAIN_ID);
  //   console.log(window.location)
  //   console.log(window.location.pathname)
  //   if (store.getters['auth/isLoggedIn']) {
  //     window.location.pathname = '/'
  //     next({ name: 'home' });
  //     // return
  //   }
  // }

  // console.log(to);
  // if (to.name !== 'page-login') {
  //   if (!store.getters['auth/isLoggedIn']) {
  //     next({ name: 'page-login' });
  //   }
  // }
  if (to.name === 'appointment') {
    console.log(to.query.id);
    if (typeof to.query.id !== 'undefined' && typeof to.query.token !== 'undefined') {
      localStorage.setItem('domainId', to.query.id);
      localStorage.setItem('accessToken', to.query.token);
      localStorage.setItem('fromToken', "1");
      localStorage.setItem('toUrl', 'appointment');
      if (store.getters['auth/isLoggedIn'] == true) {
        localStorage.removeItem('toUrl');
        next({ name: 'appointment', 'query': { 'app': to.query.app, 'date': to.query.date } });
      } else {
        next({ name: 'page-login' });
      }
    }
  } else
  if (to.name === 'pos-appointment') {
    if (typeof to.query.id !== 'undefined' && typeof to.query.token !== 'undefined') {
      localStorage.setItem('domainId', to.query.id);
      localStorage.setItem('accessToken', to.query.token);
      localStorage.setItem('fromToken', "1");
      localStorage.setItem('toUrl', 'pos-appointment');
      store.commit('auth/save_token', to.query.token);
      // store.commit('auth/SET_BEARER', to.query.token);
      await store.dispatch("auth/GET_DOMAIN", to.query.id).then(() => {
        store.commit('auth/SET_BEARER', to.query.token);
        if (store.getters['auth/isLoggedIn'] == true) {
          localStorage.removeItem('toUrl');
          next({ name: 'pos-appointment', 'query': { 'app': to.query.app, 'date': to.query.date } });
        } else {
          next({ name: 'page-login' });
        }
      });
    }
  } else
  // if (to.name === 'after-login') {
  //   if (!store.getters['auth/isLoggedIn']) {
  //     next({ name: 'page-login' });
  //   }
  // }
  if (to.name !== 'page-login') {
    if (!store.getters['auth/isLoggedIn']) {
      next({ name: 'page-login' });
    }
  } else
  if (to.name === 'page-login') {
    if (Object.keys(to.query).length == 0) {
      if (store.getters['auth/isLoggedIn'] && localStorage.getItem("fromToken") != "1") {
        let url = localStorage.getItem('toUrl');
        if (url != null) {
          next({ name: url, 'query': { 'app': 1 } });
          localStorage.removeItem('toUrl');
        } else {
          next({ name: 'after-login' });
        }
      } else {
        next();
      }
    }
    else {
      //http://localhost:8080/pages/login?id=newgci&token=BtDHv8cTCZy5IFOnO6QBF-U-XgLRkcji
      console.log(to.query);
      localStorage.setItem('domainId', to.query.id);
      localStorage.setItem('accessToken', to.query.token);
      localStorage.setItem('insight_id', to.query.insight_id);
      localStorage.setItem('fromToken', "1");
      next({ name: 'page-login' });
    }
  }
  // if(to.name != 'page-login') {
  //   if (store.getters['auth/isLoggedIn'] == false) {
  //     next({name:'page-login'});
  //     return;
  //   }
  // } else if(to.name == 'page-login') {
  //   if (store.getters['auth/isLoggedIn'] == true) {
  //     next({name:'home'});
  //     return;
  //   }
  // }
  next();
})

router.afterEach(() => {
  // Remove initial loading
  const appLoading = document.getElementById('loading-bg')
  if (appLoading) {
    appLoading.style.display = "none";
  }
})

export default router
